import { BRAND_PRIMARY_COLOR } from '@/constants/brand';

type RGB = [number, number, number];
const WHITE: RGB = [255, 255, 255];
const BLACK: RGB = [0, 0, 0];
const MIN_CONTRAST = 4.5;

const mix = (color: RGB, target: RGB, weight: number): RGB => color.map(
  (channel, index) => Math.round(channel + (target[index] - channel) * weight),
) as RGB;

const toHex = (color: RGB) => `#${color.map((channel) => channel.toString(16).padStart(2, '0')).join('')}`;

// 主题使用六位 HEX；背景还需处理 Ant Design 的半透明悬停色。
const toRGB = (color: string, background: RGB = WHITE): RGB => {
  if (/^#[0-9a-f]{6}$/i.test(color)) {
    return [1, 3, 5].map((offset) => Number.parseInt(color.slice(offset, offset + 2), 16)) as RGB;
  }
  const rgba = /^rgba?\(([\d.,\s]+)\)$/.exec(color);
  if (rgba) {
    const [r, g, b, alpha = 1] = rgba[1].split(',').map(Number);
    return mix(background, [r, g, b], alpha);
  }
  return background;
};

const luminance = (color: RGB) => {
  const [r, g, b] = color.map((channel) => {
    const value = channel / 255;
    return value <= 0.04045 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

const contrast = (foreground: RGB, background: RGB) => {
  const a = luminance(foreground);
  const b = luminance(background);
  return (Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05);
};

// 优先保留页面使用的深字 / 白字，中间亮度的背景必要时使用纯黑。
const buttonText = (background: RGB) => {
  const dark = toRGB('#16202e');
  if (contrast(dark, background) >= MIN_CONTRAST) return toHex(dark);
  return contrast(WHITE, background) >= MIN_CONTRAST ? '#ffffff' : '#000000';
};

const readableLink = (color: RGB, backgrounds: RGB[]) => {
  const readable = (candidate: RGB) => backgrounds.every(
    (background) => contrast(candidate, background) >= MIN_CONTRAST,
  );
  if (readable(color)) return toHex(color);
  const target = readable(BLACK) ? BLACK : WHITE;
  // 保留色相方向，只在需要时逐步压暗或提亮；对取整后的实际颜色检查。
  for (let step = 1; step <= 100; step += 1) {
    const candidate = mix(color, target, step / 100);
    if (readable(candidate)) return toHex(candidate);
  }
  return toHex(target);
};

/** Auth 始终使用默认品牌蓝，文字对比度随当前主题背景调整。 */
export const getAuthPalette = (
  surface: string,
  hoverSurface: string,
) => {
  const primary = BRAND_PRIMARY_COLOR;
  const background = toRGB(primary);
  const hover = mix(background, BLACK, 0.15);
  const active = mix(background, BLACK, 0.25);
  const page = toRGB(surface);
  const isDark = luminance(page) < 0.5;
  const wash = isDark ? hoverSurface : '#F6F8FA';
  return {
    primary,
    primaryText: buttonText(background),
    hover: toHex(hover),
    hoverText: buttonText(hover),
    active: toHex(active),
    activeText: buttonText(active),
    link: readableLink(background, [page, toRGB(wash, page)]),
    wash,
    isDark,
  };
};
